import { makeStyles } from 'tss-react/mui';
import { AddressSuggestionType, AddressType } from '@uplift/types';
import { useField } from 'formik';
import React, { useMemo, useRef } from 'react';
import { BaseTextFieldProps, SearchAndSelect } from '../../Inputs';
import { AddressSearchListItem } from '../AddressSearchList/AddressSearchListItem.component';
import { useFetchAddressSuggestions } from './../AddressSearchModal';

export type AddressSearchInputType = {
  name: string;
  label: string;
  onAddressSelect: (address: AddressType) => void;
  startSearch?: boolean;
  inputClasses?: BaseTextFieldProps['inputClasses'];
  width?: string;
  maxWidth?: string;
};

export const AddressSearchInput: React.VFC<AddressSearchInputType> = ({
  name = 'address1',
  label = 'Street Address',
  onAddressSelect,
  inputClasses,
  width,
  maxWidth,
}) => {
  const initialRef = useRef(true);
  const { classes, cx } = useStyles({ width, maxWidth });
  const [ field, meta, _helpers ] = useField<string | undefined>(name);

  const value = field.value ?? '';

  // this will circumvent unnecessary inital fetch
  const startSearch = useMemo(() => {
    if (initialRef.current) {
      initialRef.current = false;
      return false;
    }
    return value !== meta.initialValue || !initialRef.current;
  }, [ value, meta.initialValue ]);

  // trim and replace all non-alphaNumeric chars (that aren't spaces)
  const searchValue: string = value.trim().replace(/[^A-Za-z0-9 ]/g, '');

  const showResults: boolean = useMemo(() => {
    return searchValue.length >= 6 && startSearch;
  }, [ searchValue, startSearch ]);
  const { suggestions, loading: _loading } = useFetchAddressSuggestions({
    text: searchValue,
    constraint: showResults,
    debounceMs: 400
  });

  const addressSelectHandler = (rowId: string) => {
    const addressSuggestion: AddressSuggestionType = suggestions[Number(rowId)];
    const address = addressSuggestion.address;

    onAddressSelect(address);
  };

  const rows = useMemo(() => suggestions
    .map(({ address: { address1, address2, city, state, zip } }, i) => ({
      id: `${i}`,
      address: `${address1 ?? ''} ${address2 ?? ''}`,
      addressCityState: `${city ?? ''} ${state ?? ''} ${zip ?? ''}`
    })),
  [ suggestions ]);

  return (
    (
      <SearchAndSelect
        inputClasses={inputClasses}
        className={classes.width}
        fullWidth={!width}
        width={width}
        maxWidth={maxWidth}
        id={name}
        name={name}
        label={label}
        options={rows}
        popperConstraint={rows.length !== 0}
        listProps={{ disablePadding: true, className: cx(classes.list, classes.width) }}
        renderListItem={(option) => {
          return (
            <AddressSearchListItem
              row={option}
              searchValue={searchValue}
              onAddressSelect={addressSelectHandler}
            />
          );
        }}
      />
    )
  );
};

type SearchInputStyleProps = {
  width?: string;
  maxWidth?: string;
};

const useStyles = makeStyles<SearchInputStyleProps>()((theme, { width, maxWidth }) => ({
  list: {
    paddingTop: theme.spacing(1)
  },
  width: {
    width: width ? width : undefined,
    maxWidth: maxWidth ? maxWidth : undefined,
  }
}));
